import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import PreviewFeeStructure from "./PreviewFeeStructure";
import DeployedFeeStructures from "./DeployedFeeStructures";

import { db } from "../firebase/config";
import {
  collection,
  doc,
  setDoc,
  query,
  getDoc,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  IdTokenResult,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CreateFeeStructureProps {
  schoolId: string | null;
  onBack: () => void;
}

interface FeeCategory {
  name: string;
  term1: number;
  term2: number;
  term3: number;
  paidOnce?: boolean;
  daySchoolTerm1?: number;
  daySchoolTerm2?: number;
  daySchoolTerm3?: number;
  feesAreEqual?: boolean;
}

export interface LevelFees {
  feeType: string;
  classes: string[];
  categories: FeeCategory[];
  insights?: string;
  importantNotes?: string;
  feeStructureUid?: string;
  createdAt?: Date;
  submittedBy?: string;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
}

/* ── Level → Numeric Mapping (matches ClassRegistrationForm) ── */

const CreateFeeStructure: React.FC<CreateFeeStructureProps> = ({
  schoolId: propSchoolId,
  onBack,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  /* ── Tab State ─────────────────────────────────────────── */
  const [activeTab, setActiveTab] = useState<"create" | "preview" | "deployed">("create");

  /* ── Auth & Claims ─────────────────────────────────────── */
  const [userId, setUserId] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        try {
          await user.getIdToken(true);
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          let selectedAff: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAff = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAff = affiliations.find((a) => a.role === "admin" && a.schoolID) || affiliations[0];
          }

          if (
            selectedAff?.role === "admin" &&
            selectedAff.schoolID &&
            claimSchoolID === selectedAff.schoolID
          ) {
            setSchoolID(selectedAff.schoolID);
            setHasValidClaims(true);
          } else {
            await new Promise((r) => setTimeout(r, 10000));
            await user.getIdToken(true);
            const retry = await user.getIdTokenResult(true);
            const rAff = (retry.claims.affiliations as Affiliation[] | undefined) || [];
            const rDef = retry.claims.defaultAffiliation as Affiliation | undefined;
            const rSch = retry.claims.schoolID as string | undefined;

            const rSel = rDef?.role === "admin" && rDef.schoolID
              ? rDef
              : rAff.find((a) => a.role === "admin" && a.schoolID) || rAff[0];

            if (rSel?.role === "admin" && rSel.schoolID && rSch === rSel.schoolID) {
              setSchoolID(rSel.schoolID);
              setHasValidClaims(true);
            } else {
              throw new Error("Invalid admin claims");
            }
          }
        } catch (err) {
          console.error(err);
          toast({
            title: "Missing Permissions",
            description: "You lack admin rights for this school.",
            variant: "destructive",
            duration: 5000,
          });
          setHasValidClaims(false);
          navigate("/login-page");
        }
      } else {
        setUserId(null);
        setSchoolID(null);
        setHasValidClaims(false);
        toast({
          title: "Authentication Error",
          description: "Please log in.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  /* ── Form State ────────────────────────────────────────── */
  const [feeType, setFeeType] = useState("");
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [insights, setInsights] = useState("");
  const [importantNotes, setImportantNotes] = useState("");
  const [insightsSet, setInsightsSet] = useState(false);
  const [importantNotesSet, setImportantNotesSet] = useState(false);

  /* ── Bullet List State ────────────────────────────────── */
  const [insightsList, setInsightsList] = useState<string[]>([]);
  const [currentInsight, setCurrentInsight] = useState("");
  const [importantNotesList, setImportantNotesList] = useState<string[]>([]);
  const [currentNote, setCurrentNote] = useState("");

  const addInsight = () => {
    if (currentInsight.trim()) {
      setInsightsList([...insightsList, currentInsight.trim()]);
      setCurrentInsight("");
    }
  };

  const removeInsight = (index: number) => {
    setInsightsList(insightsList.filter((_, i) => i !== index));
  };

  const addNote = () => {
    if (currentNote.trim()) {
      setImportantNotesList([...importantNotesList, currentNote.trim()]);
      setCurrentNote("");
    }
  };

  const removeNote = (index: number) => {
    setImportantNotesList(importantNotesList.filter((_, i) => i !== index));
  };

  const [categories, setCategories] = useState<FeeCategory[]>([
    { name: "", term1: 0, term2: 0, term3: 0, paidOnce: false, feesAreEqual: false },
  ]);
  const [isGovernmentSponsored, setIsGovernmentSponsored] = useState(false);
  const [governmentSponsorshipAmount, setGovernmentSponsorshipAmount] = useState(0);

  /* ── Check for existing uniform fee structure ──────────── */
  const [hasUniformFee, setHasUniformFee] = useState(false);
  const [, setCheckingExisting] = useState(false);
  const [allowOverwrite, setAllowOverwrite] = useState(false);

  /* ── Track classes with fees ──────────────────────────── */
  const [classesWithFees, setClassesWithFees] = useState<Set<string>>(new Set());

  /* ── Load School Type ──────────────────────────────────── */
  const [schoolType, setSchoolType] = useState<string>("");

  /* ── Class Data & Hierarchy Stats ──────────────────────── */
  // We now store full class objects to handle level/namingConvention logic correctly
  interface SimpleClassData {
    name: string;
    level: number;
    namingConvention: string;
  }
  
  const [fullClasses, setFullClasses] = useState<SimpleClassData[]>([]);
  const [conventionStats, setConventionStats] = useState<Record<string, { min: number; max: number; totalTerms: number }>>({});

  useEffect(() => {
    if (fullClasses.length === 0) {
      setConventionStats({});
      return;
    }

    // Group by naming convention
    const groups: Record<string, number[]> = {};
    
    fullClasses.forEach(cls => {
      // Default to "Unknown" if missing namingConvention (legacy data safety)
      const conv = cls.namingConvention || "Standard"; 
      if (!groups[conv]) groups[conv] = [];
      groups[conv].push(cls.level);
    });

    const stats: Record<string, { min: number; max: number; totalTerms: number }> = {};
    
    Object.entries(groups).forEach(([conv, levels]) => {
      if (levels.length > 0) {
        const uniqueLevels = Array.from(new Set(levels)).sort((a,b) => a-b);
        const min = uniqueLevels[0];
        const max = uniqueLevels[uniqueLevels.length - 1];
        // Calculate terms: (max - min + 1) * 3
        const totalTerms = (max - min + 1) * 3;
        stats[conv] = { min, max, totalTerms };
      }
    });

    setConventionStats(stats);
  }, [fullClasses]);

  /* ── Load Real Classes from Firestore ──────────────────── */
  const [realClasses, setRealClasses] = useState<string[]>([]);
  const [groupedClasses, setGroupedClasses] = useState<Record<string, string[]>>({});
  const [classesLoading, setClassesLoading] = useState(false);

  // Check if a uniform fee structure already exists and load classes with fees
  useEffect(() => {
    if (!schoolID) return;

    const checkExistingFeeStructures = async () => {
      setCheckingExisting(true);
      try {
        const q = query(collection(db, `schools/${schoolID}/feeStructures`));
        const snap = await getDocs(q);
        
        let foundUniform = false;
        const classesWithFeesSet = new Set<string>();
        
        snap.forEach((doc) => {
          const data = doc.data();
          console.log("CreateFeeStructure - Loaded fee structure:", data);
          if (data.feeType === "uniform") {
            foundUniform = true;
          }
          // Track which classes already have fees (from the classes array)
          if (data.classes && Array.isArray(data.classes)) {
            data.classes.forEach((className: string) => {
              classesWithFeesSet.add(className);
            });
          }
        });
        
        console.log("CreateFeeStructure - Classes with fees:", Array.from(classesWithFeesSet));
        setHasUniformFee(foundUniform);
        setClassesWithFees(classesWithFeesSet);
      } catch (e) {
        console.error("Error checking existing fee structures:", e);
      } finally {
        setCheckingExisting(false);
      }
    };

    checkExistingFeeStructures();
  }, [schoolID]);

  // Load school type from Firestore
  useEffect(() => {
    if (!schoolID) return;

    const loadSchoolType = async () => {
      try {
        const schoolDetailsRef = doc(db, `schools/${schoolID}/schoolDetails/main`);
        const schoolDetailsDoc = await getDoc(schoolDetailsRef);
        if (schoolDetailsDoc.exists()) {
          const schoolData = schoolDetailsDoc.data();
          setSchoolType(schoolData.type || "");
          console.log("CreateFeeStructure - School type:", schoolData.type);
        }
      } catch (e) {
        console.error("Error loading school type:", e);
      }
    };

    loadSchoolType();
  }, [schoolID]);

  useEffect(() => {
    if (!schoolID) {
      setRealClasses([]);
      setGroupedClasses({});
      return;
    }

    const loadClasses = async () => {
      setClassesLoading(true);
      try {
        const q = query(collection(db, `schools/${schoolID}/classes`));
        const snap = await getDocs(q);
        
        // Extract needed fields
        const loadedClasses = snap.docs.map(d => {
            const data = d.data();
            return {
                name: data.name as string,
                level: typeof data.level === 'number' ? data.level : 0,
                namingConvention: (data.namingConvention as string) || "Standard"
            };
        });
        
        setFullClasses(loadedClasses);

        const names = loadedClasses.map((d) => d.name).sort();
        setRealClasses(names);

        const groups: Record<string, string[]> = {};
        names.forEach((name) => {
          const parts = name.split(" ");
          let groupName = name;
          if (parts.length > 1) {
            const lastPart = parts[parts.length - 1];
            if (isNaN(parseInt(lastPart, 10))) {
              // If the last part is not a number, it's likely a stream name
              // But we need to be careful not to strip '1' from 'Form 1' if '1' is the level
              // Logic check: usually classes are 'Form 1 Blue'
              // groupName should be 'Form 1'
              
              // Refined logic: use the loaded namingConvention and level to rebuild group name for reliability?
              // Or stick to existing logic for UI consistence? 
              // Existing logic: parts.slice(0, -1).join(" ")
              groupName = parts.slice(0, -1).join(" ");
            }
          }

          if (!groups[groupName]) {
            groups[groupName] = [];
          }
          groups[groupName].push(name);
        });
        setGroupedClasses(groups);
      } catch (e) {
        console.error(e);
        toast({
          title: "Error",
          description: "Could not load classes.",
          variant: "destructive",
        });
      } finally {
        setClassesLoading(false);
      }
    };

    loadClasses();
  }, [schoolID, toast]);

  // Auto-load custom fee type if there are classes with fees but not all classes are covered
  useEffect(() => {
    if (!schoolID || classesLoading || Object.keys(groupedClasses).length === 0) return;
    
    const totalClasses = Object.keys(groupedClasses).length;
    const classesWithFeesCount = classesWithFees.size;
    
    // If some classes have fees but not all, auto-select custom fee type
    if (classesWithFeesCount > 0 && classesWithFeesCount < totalClasses && !feeType && !hasUniformFee) {
      setFeeType('custom');
    }
  }, [schoolID, classesLoading, groupedClasses, classesWithFees, feeType, hasUniformFee]);

  useEffect(() => {
    if (feeType === 'uniform' && realClasses.length > 0) {
      // For uniform fees, register base class names only (without streams)
      const baseClasses = [...new Set(
        realClasses.map(cls => {
          const parts = cls.split(" ");
          if (parts.length > 1) {
            const lastPart = parts[parts.length - 1];
            const streamNames = ["Red", "Blue", "Green", "Yellow", "North", "South", "East", "West", "A", "B", "C", "D"];
            if (streamNames.includes(lastPart)) {
              return parts.slice(0, -1).join(" ");
            }
          }
          return cls;
        })
      )];
      setSelectedClasses(baseClasses);
    }
  }, [feeType, realClasses]);

  const toggleGroup = (groupName: string) => {
    if (feeType === 'uniform') return; // No changes allowed for uniform fee

    // For custom fees, select the base class name only (without streams)
    const baseClassName = groupName;
    const isSelected = selectedClasses.includes(baseClassName);

    if (isSelected) {
      setSelectedClasses((prev) => prev.filter((c) => c !== baseClassName));
    } else {
      setSelectedClasses((prev) => [...prev, baseClassName]);
    }
  };

  const addCategory = () => {
    setCategories([...categories, { name: "", term1: 0, term2: 0, term3: 0, paidOnce: false, feesAreEqual: false }]);
  };

  const removeCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const updateCategory = (
    index: number,
    field: keyof FeeCategory,
    value: string | number | boolean
  ) => {
    const updated = [...categories];
    if (field === "paidOnce" || field === "feesAreEqual") {
      updated[index][field] = value as boolean;
    } else if (field === "name") {
      updated[index][field] = value as string;
    } else {
      // Numeric fields: term1, term2, term3, daySchoolTerm1, daySchoolTerm2, daySchoolTerm3
      (updated[index] as any)[field] = Number(value) || 0;
    }
    setCategories(updated);
  };

  /* ── Submit with Duplicate Check ────────────────────────── */
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!schoolID || !userId || !hasValidClaims) {
      toast({
        title: "Authentication Error",
        description: "Missing school/admin permissions.",
        variant: "destructive",
      });
      navigate("/login-page");
      return;
    }

    if (!feeType) {
      toast({ title: "Error", description: "Please select a fee type", variant: "destructive" });
      return;
    }
    if (selectedClasses.length === 0) {
      toast({ title: "Error", description: "Select at least one class", variant: "destructive" });
      return;
    }
    const validCategories = categories.filter((c) => c.name.trim() !== "");
    if (validCategories.length === 0) {
      toast({ title: "Error", description: "Add at least one fee category", variant: "destructive" });
      return;
    }

    const user = getAuth().currentUser;
    if (!user) return;

    setSaving(true);
    try {
      // Re-validate claims
      await user.getIdToken(true);
      const token = await user.getIdTokenResult(true);
      const affs = (token.claims.affiliations as Affiliation[] | undefined) || [];
      const def = token.claims.defaultAffiliation as Affiliation | undefined;
      const sch = token.claims.schoolID as string | undefined;

      const adminAff = affs.find((a) => a.role === "admin" && a.schoolID === schoolID);
      if (
        !adminAff &&
        (!def || def.role !== "admin" || def.schoolID !== schoolID) &&
        sch !== schoolID
      ) {
        throw new Error("Claim mismatch on submit");
      }

      // Prevent duplicate fee structure for any of the selected classes (unless overwriting)
      if (!allowOverwrite) {
        const existingCheck = query(collection(db, `schools/${schoolID}/feeStructures`));
        const existingSnap = await getDocs(existingCheck);

        let isConflict = false;
        existingSnap.forEach((doc) => {
          const existing = doc.data();
          const existingClasses = existing.classes || [];
          const overlap = selectedClasses.some((c: string) => existingClasses.includes(c));
          if (overlap) {
            isConflict = true;
          }
        });

        if (isConflict) {
          toast({
            title: "Warning",
            description: `A fee structure for this class has already been created.`,
            variant: "destructive",
          });
          setSaving(false);
          return;
        }
      } else {
        // Delete existing fee structures if overwriting
        const existingCheck = query(collection(db, `schools/${schoolID}/feeStructures`));
        const existingSnap = await getDocs(existingCheck);
        
        const deletePromises: Promise<void>[] = [];
        existingSnap.forEach((docSnapshot) => {
          deletePromises.push(deleteDoc(docSnapshot.ref));
        });
        
        await Promise.all(deletePromises);
      }

      const feeColRef = collection(db, `schools/${schoolID}/feeStructures`);
      const newDocRef = doc(feeColRef);

      const payload: any = {
        feeType,
        classes: selectedClasses,
        categories: validCategories,
        feeStructureUid: newDocRef.id,
        createdAt: new Date(),
        submittedBy: userId,
        isGovernmentSponsored,
        governmentSponsorshipAmount: isGovernmentSponsored ? governmentSponsorshipAmount : 0,
      };

      // Process Insights and Notes into bulleted strings
      const finalInsights = insightsList.length > 0 
        ? insightsList.map(i => `• ${i}`).join('\n')
        : insights.trim(); // Fallback to raw string if used (though UI now enforces list)

      const finalNotes = importantNotesList.length > 0
        ? importantNotesList.map(n => `• ${n}`).join('\n')
        : importantNotes.trim();

      // Only add if they have values
      if (finalInsights) {
        payload.insights = finalInsights;
      }
      if (finalNotes) {
        payload.importantNotes = finalNotes;
      }

      // Generate Term IDs for each selected class
      const termIdsByClass: Record<string, { term1: number; term2: number; term3: number }> = {};
      
      selectedClasses.forEach((clsName) => {
        // clsName might be a group name (e.g. "Form 1") or a specific class name.
        // We need to find *one* representative real class to get the level/naming convention.
        let realName = clsName;
        if (groupedClasses[clsName] && groupedClasses[clsName].length > 0) {
            realName = groupedClasses[clsName][0];
        }

        const clsObj = fullClasses.find(c => c.name === realName);
        
        if (clsObj) {
            const conv = clsObj.namingConvention;
            const stats = conventionStats[conv];
            // Use the stored min from stats, or default to current level if strictly isolated
            // But usually we want the min of the whole cycle.
            const minLevelForConv = stats ? stats.min : 1;
            
            // Term ID calculation: (CurrentLevel - MinLevelOfCycle) * 3 + Term
            const base = (clsObj.level - minLevelForConv) * 3;
            
            termIdsByClass[clsName] = {
                term1: base + 1,
                term2: base + 2,
                term3: base + 3
            };
        } else {
            console.warn(`Class object not found for ${clsName} (real: ${realName})`);
            // Fallback
            const match = realName.match(/\d+/);
            const level = match ? parseInt(match[0], 10) : 1;
            const base = (level - 1) * 3;
            termIdsByClass[clsName] = { 
                term1: base + 1, 
                term2: base + 2, 
                term3: base + 3 
            };
        }
      });
      payload.termIdsByClass = termIdsByClass;

      await setDoc(newDocRef, payload);

      // Update classesWithFees state
      const updatedClassesWithFees = new Set(classesWithFees);
      selectedClasses.forEach(cls => updatedClassesWithFees.add(cls));
      setClassesWithFees(updatedClassesWithFees);

      // Reset form
      setSelectedClasses([]);
      setCategories([{ name: "", term1: 0, term2: 0, term3: 0, paidOnce: false }]);
      setInsights("");
      setImportantNotes("");
      setInsightsList([]);
      setImportantNotesList([]);
      setInsightsSet(false);
      setImportantNotesSet(false);
      setIsGovernmentSponsored(false);
      setGovernmentSponsorshipAmount(0);

      toast({ title: "Success", description: "Fee structure saved to Firestore" });
      
      // Check if all classes have fees now
      const allClassesHaveFees = Object.keys(groupedClasses).every(cls => 
        updatedClassesWithFees.has(cls)
      );
      
      if (allClassesHaveFees) {
        toast({ 
          title: "All Classes Covered", 
          description: "All classes now have fee structures assigned.",
          variant: "default"
        });
      }
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Save Failed",
        description: err.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── UI Guards ─────────────────────────────────────────── */
  if (isAuthLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!hasValidClaims) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="m-2 bg-white dark:bg-[#020817] rounded-lg shadow border border-gray-200 dark:border-gray-600">
          <CardHeader className="bg-gray-50 dark:bg-[#020817]/50 border-b border-gray-200 dark:border-gray-600">
            <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
              Access Denied
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-red-600 dark:text-red-400 mb-4">
              You do not have admin rights for this school.
            </p>
            <Button
              onClick={() => navigate("/login-page")}
              className="bg-gray-900 dark:bg-[#020817] hover:bg-gray-800 dark:hover:bg-[#020817]/80 text-white"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!propSchoolId) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="m-2 bg-white dark:bg-[#020817] rounded-lg shadow border border-gray-200 dark:border-gray-600">
          <CardHeader className="bg-gray-50 dark:bg-[#020817]/50 border-b border-gray-200 dark:border-gray-600">
            <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Error</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-red-600 dark:text-red-400 mb-4">School ID is not available.</p>
            <Button
              onClick={onBack}
              className="bg-gray-900 dark:bg-[#020817] hover:bg-gray-800 dark:hover:bg-[#020817]/80 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Finance
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── Main UI ─────────────────────────────────────────────── */
  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-center py-2">
        <div className="w-1/2 max-w-xs p-1 space-x-1 rounded-lg bg-gray-200 dark:bg-[#020817] border border-gray-200 dark:border-gray-600 flex">
          <button
            onClick={() => setActiveTab("create")}
            className={`w-full rounded-lg py-2 text-sm font-medium leading-5 transition-all ${
              activeTab === "create"
                ? "text-white bg-gray-900 dark:bg-gray-700 shadow"
                : "text-gray-700 dark:text-gray-300 hover:bg-white/[0.12] hover:text-white"
            }`}
          >
            Create
          </button>
          <button
            onClick={() => setActiveTab("preview")}
            className={`w-full rounded-lg py-2 text-sm font-medium leading-5 transition-all ${
              activeTab === "preview"
                ? "text-white bg-gray-900 dark:bg-gray-700 shadow"
                : "text-gray-700 dark:text-gray-300 hover:bg-white/[0.12] hover:text-white"
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab("deployed")}
            className={`w-full rounded-lg py-2 text-sm font-medium leading-5 transition-all ${
              activeTab === "deployed"
                ? "text-white bg-gray-900 dark:bg-gray-700 shadow"
                : "text-gray-700 dark:text-gray-300 hover:bg-white/[0.12] hover:text-white"
            }`}
          >
            Deployed
          </button>
        </div>
      </div>

      {activeTab === "preview" ? (
        <>
          <div className="flex items-center m-2 gap-4">
            <Button
              onClick={onBack}
              variant="outline"
              className="border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
          <PreviewFeeStructure />
        </>
      ) : activeTab === "deployed" ? (
        <DeployedFeeStructures />
      ) : (
        <>
          <div className="flex items-center m-2 gap-4">
            <Button
              onClick={onBack}
              variant="outline"
              className="border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 flex-wrap">
                Create Fee Structure
                {Object.entries(conventionStats).map(([conv, stats]) => (
                  <span key={conv} className="text-base font-medium text-gray-500 dark:text-gray-400 border px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800">
                    {conv}: {stats.totalTerms} Terms
                  </span>
                ))}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Define a comprehensive fee structure for School ID:{" "}
                <span className="font-mono font-semibold">{propSchoolId}</span>
              </p>
            </div>
          </div>

          <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
        <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {hasUniformFee && (
            <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                        Uniform Fee Structure Exists
                      </p>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="overwrite-toggle" className="text-xs font-medium text-amber-800 dark:text-amber-300 cursor-pointer">
                          Allow Overwrite
                        </Label>
                        <Switch
                          id="overwrite-toggle"
                          checked={allowOverwrite}
                          onCheckedChange={setAllowOverwrite}
                          className="data-[state=checked]:bg-amber-600"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                      {allowOverwrite 
                        ? "Overwrite mode enabled. Creating a new fee structure will replace the existing uniform fee structure."
                        : "A uniform fee structure already exists that applies to all classes. You cannot create additional fee structures. Please delete the existing uniform fee structure from the Preview tab if you want to create class-specific fees."
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="space-y-2">
            <Label htmlFor="feeType" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Fee applies to
            </Label>
            <Select 
              value={feeType} 
              onValueChange={(value) => { setFeeType(value); setSelectedClasses([]); }}
              disabled={hasUniformFee && !allowOverwrite}
            >
              <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
                <SelectValue placeholder="Select fee type" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                <SelectItem value="uniform" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  Apply Same Fee to All Classes
                </SelectItem>
                <SelectItem value="custom" className="text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700">
                  Set Custom Fee per Class
                </SelectItem>
              </SelectContent>
            </Select>
            {hasUniformFee && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Fee structure creation is disabled because a uniform fee structure already exists.
              </p>
            )}
          </div>

                    {feeType && (
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Classes Covered *
                        </Label>
          
                        {classesLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-600 dark:text-gray-400" />
                            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                              Loading classes...
                            </span>
                          </div>
                        ) : Object.keys(groupedClasses).length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            No classes found for this school. Please register classes first.
                          </p>
                        ) : (
                          <>
                            {feeType === 'custom' && classesWithFees.size > 0 && classesWithFees.size < Object.keys(groupedClasses).length && (
                              <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 mb-4">
                                <CardContent className="pt-4 pb-4">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs font-semibold text-red-900 dark:text-red-200 mb-1">
                                        Warning: Incomplete Fee Structure
                                      </p>
                                      <p className="text-xs text-red-800 dark:text-red-300">
                                        {Object.keys(groupedClasses).filter(cls => !classesWithFees.has(cls)).join(", ")} {Object.keys(groupedClasses).filter(cls => !classesWithFees.has(cls)).length === 1 ? "has" : "have"} no fee structure. Please set fees for all classes.
                                      </p>
                                    </div>
                                  </div>
                                </CardContent>
                              </Card>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {Object.keys(groupedClasses).map((groupName) => {
                                const isSelected = selectedClasses.includes(groupName);
                                const hasFee = classesWithFees.has(groupName);
                                const isDisabled = feeType === 'custom' && hasFee;
          
                                return (
                                  <div
                                    key={groupName}
                                    onClick={() => !isDisabled && toggleGroup(groupName)}
                                    className={`
                                    flex items-center justify-center p-3 rounded-md border-2 transition-all text-center text-xs relative
                                    ${
                                      isSelected
                                        ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium"
                                        : hasFee
                                        ? "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                                        : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                                    }
                                    ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                                    ${feeType === 'uniform' ? 'opacity-60 cursor-not-allowed' : ''}
                                  `}
                                  >
                                    {groupName}
                                    {hasFee && feeType === 'custom' && (
                                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 dark:bg-green-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold">
                                        ✓
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
          
                            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-md">
                              <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                                {feeType === 'uniform'
                                  ? <>All selected classes will have <strong>identical fees</strong>. This ensures fairness.</>
                                  : <>Select a class or group to create a <strong>unique fee structure</strong> for.</>
                                }
                              </p>
                            </div>
                          </>
                        )}
          
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Tip: Select all streams in a level (e.g., Form 1 Red + Blue) to apply the same fees. 
                          Create a new structure only if a different level has different fees.
                        </p>
                      </div>
                    )}        </CardContent>
          </Card>

          {/* ── Fee Categories (unchanged) ── */}
          <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
        <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Fee Categories</CardTitle>
            <Button onClick={addCategory} size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4" />
              Add Category
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {categories.map((category, index) => (
            <Card key={index} className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Category Name *
                    </Label>
                    <Input
                      placeholder="e.g., Tuition Fees"
                      value={category.name}
                      onChange={(e) => updateCategory(index, "name", e.target.value)}
                      className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                    />
                  </div>
                  {categories.length > 1 && (
                    <Button
                      onClick={() => removeCategory(index)}
                      size="icon"
                      variant="ghost"
                      className="mt-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Checkbox for equal fees - Only show if school type is Mixed */}
                {schoolType === "Mixed (Day & Boarding)" && (
                  <div className="flex items-center space-x-2 pb-2">
                    <Checkbox
                      id={`fees-equal-${index}`}
                      checked={category.feesAreEqual}
                      onCheckedChange={(checked) => updateCategory(index, "feesAreEqual", checked === true)}
                    />
                    <Label htmlFor={`fees-equal-${index}`} className="text-sm cursor-pointer text-gray-700 dark:text-gray-300">
                      Fees are equal for boarding and day school students in this category
                    </Label>
                  </div>
                )}

                {/* Show single fee row if fees are equal, otherwise show both rows */}
                {schoolType === "Mixed (Day & Boarding)" && !category.feesAreEqual ? (
                  <>
                    {/* Boarding School Fee Row */}
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-blue-700 dark:text-blue-400 block">
                        Boarding School Fee
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 1 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.term1 || ""}
                            onChange={(e) => updateCategory(index, "term1", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 2 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.term2 || ""}
                            onChange={(e) => updateCategory(index, "term2", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 3 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.term3 || ""}
                            onChange={(e) => updateCategory(index, "term3", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Day School Fee Row */}
                    <div className="pt-2 border-t border-gray-300 dark:border-gray-600 space-y-3">
                      <Label className="text-sm font-semibold text-blue-700 dark:text-blue-400 block">
                        Day School Fee
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 1 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.daySchoolTerm1 || ""}
                            onChange={(e) => updateCategory(index, "daySchoolTerm1", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            placeholder="Day school fee for Term 1"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 2 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.daySchoolTerm2 || ""}
                            onChange={(e) => updateCategory(index, "daySchoolTerm2", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            placeholder="Day school fee for Term 2"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Term 3 (KSH)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            value={category.daySchoolTerm3 || ""}
                            onChange={(e) => updateCategory(index, "daySchoolTerm3", e.target.value)}
                            className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                            placeholder="Day school fee for Term 3"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        These fees apply specifically to day school students.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Single fee row when fees are equal or school is not Mixed */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Term 1 (KSH)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={category.term1 || ""}
                          onChange={(e) => updateCategory(index, "term1", e.target.value)}
                          className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Term 2 (KSH)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={category.term2 || ""}
                          onChange={(e) => updateCategory(index, "term2", e.target.value)}
                          className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Term 3 (KSH)
                        </Label>
                        <Input
                          type="number"
                          min="0"
                          value={category.term3 || ""}
                          onChange={(e) => updateCategory(index, "term3", e.target.value)}
                          className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`paid-once-${index}`}
                    checked={category.paidOnce}
                    onCheckedChange={(checked) => updateCategory(index, "paidOnce", checked === true)}
                  />
                  <Label htmlFor={`paid-once-${index}`} className="text-sm cursor-pointer text-gray-700 dark:text-gray-300">
                    One-time fee (paid only in Term 1)
                  </Label>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
          </Card>

          {/* ── Government Sponsorship ── */}
          <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
        <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
          <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Government Sponsorship</CardTitle>
          <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
            Enable this if the fee structure is for government-sponsored students.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-government-sponsored"
              checked={isGovernmentSponsored}
              onCheckedChange={(checked) => setIsGovernmentSponsored(checked === true)}
            />
            <Label htmlFor="is-government-sponsored" className="text-sm cursor-pointer text-gray-700 dark:text-gray-300">
              This fee structure is for government-sponsored students
            </Label>
          </div>
          {isGovernmentSponsored && (
            <div className="space-y-2">
              <Label htmlFor="government-sponsorship-amount" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Government Sponsorship Amount (KSH)
              </Label>
              <Input
                id="government-sponsorship-amount"
                type="number"
                min="0"
                value={governmentSponsorshipAmount || ""}
                onChange={(e) => setGovernmentSponsorshipAmount(Number(e.target.value))}
                className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                placeholder="e.g., 10000"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This amount will be deducted from the total fees for each sponsored student.
              </p>
            </div>
          )}
        </CardContent>
          </Card>

          {/* ── Insights & Notes (show only if not set) ── */}
          {!insightsSet && (
            <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
              <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Fee Structure Insights</CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                  Add key insights about this fee structure as bullet points.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {insightsList.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {insightsList.map((item, idx) => (
                      <li key={idx} className="flex items-start justify-between bg-gray-50 dark:bg-gray-700/30 p-2 rounded-md">
                        <span className="text-gray-700 dark:text-gray-300 text-sm flex-1 mr-2">• {item}</span>
                        <button onClick={() => removeInsight(idx)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                
                <div className="flex gap-2">
                  <Input 
                    placeholder="Type an insight and click Add..." 
                    value={currentInsight}
                    onChange={(e) => setCurrentInsight(e.target.value)}
                    onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addInsight(); } }}
                    className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                  <Button onClick={addInsight} variant="secondary" className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white">
                    <Plus className="h-4 w-4" /> Add
                  </Button>
                </div>

                {insightsList.length > 0 && (
                  <Button
                    onClick={() => setInsightsSet(true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white w-full mt-2"
                  >
                    Save Insights
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {!importantNotesSet && (
            <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
              <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Important Notes</CardTitle>
                <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                  Add important payment terms or conditions as bullet points.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                 {importantNotesList.length > 0 && (
                  <ul className="space-y-2 mb-4">
                    {importantNotesList.map((item, idx) => (
                      <li key={idx} className="flex items-start justify-between bg-gray-50 dark:bg-gray-700/30 p-2 rounded-md">
                        <span className="text-gray-700 dark:text-gray-300 text-sm flex-1 mr-2">• {item}</span>
                        <button onClick={() => removeNote(idx)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex gap-2">
                  <Input 
                    placeholder="Type a note and click Add..." 
                    value={currentNote}
                    onChange={(e) => setCurrentNote(e.target.value)}
                    onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addNote(); } }}
                    className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                  />
                  <Button onClick={addNote} variant="secondary" className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white">
                    <Plus className="h-4 w-4" /> Add
                  </Button>
                </div>

                {importantNotesList.length > 0 && (
                  <Button
                    onClick={() => setImportantNotesSet(true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white w-full mt-2"
                  >
                    Save Notes
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Show buttons to add insights/notes if they've been hidden */}
          {(insightsSet || importantNotesSet) && (
            <Card className="m-2 bg-gray-50 dark:bg-gray-800/50 border-dashed border-gray-300 dark:border-gray-600">
              <CardContent className="p-4 flex gap-3 justify-center flex-wrap">
                {insightsSet && (
                  <Button
                    onClick={() => setInsightsSet(false)}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {insightsList.length > 0 ? "Edit Insights" : "Add Insights"}
                  </Button>
                )}
                {importantNotesSet && (
                  <Button
                    onClick={() => setImportantNotesSet(false)}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                  >
                    {importantNotesList.length > 0 ? "Edit Important Notes" : "Add Important Notes"}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between gap-3 pb-8 m-2">
            <Button
              variant="outline"
              onClick={onBack}
              className="border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              size="lg"
              disabled={saving || (hasUniformFee && !allowOverwrite)}
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Fee Structure"
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateFeeStructure;